import { useRef } from 'react';
import { Check, RotateCcw } from 'lucide-react';
import { DisabledHint } from '@/components/common/DisabledHint';
import { FilterFieldsGrid } from '@/components/common/FilterFieldsGrid';
import { Button } from '@/components/ui/button';
import { useFilterColumnCount } from '@/hooks/useFilterColumnCount';
import { FilterFieldItem } from './FilterFieldItem';
import type { AdvancedFilterPanelProps, FilterField } from './advancedFilter.types';

const NO_DEFAULT_VALUES: Partial<Record<string, string>> = {};
const NO_REQUIRED_KEYS: readonly string[] = [];
const PANEL_BUTTON_CLASS = 'h-10 shrink-0 gap-1.5 rounded-xl';
const MISSING_LABEL_SEPARATOR = ' and ';

function toMissingValueHint(missingFields: FilterField[]): string {
  return `Pick ${missingFields.map((field) => field.label).join(MISSING_LABEL_SEPARATOR)} first`;
}

export function AdvancedFilterPanel({
  fields,
  values,
  onChange,
  defaultValues = NO_DEFAULT_VALUES,
  requiredKeys = NO_REQUIRED_KEYS,
  onApply,
  hasPendingChanges = false,
}: AdvancedFilterPanelProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const columnCount = useFilterColumnCount(gridRef);
  const valueKeys = fields.map((field) => field.key);
  const missingRequiredFields = fields.filter(
    (field) => requiredKeys.includes(field.key) && !values[field.key],
  );
  const isApplyBlocked = missingRequiredFields.length > 0;

  function handleReset() {
    const resetValues = {
      ...values,
      ...Object.fromEntries(valueKeys.map((key) => [key, defaultValues[key] ?? ''])),
    };
    if (onApply) {
      onApply(resetValues);
      return;
    }
    onChange(resetValues);
  }

  function handleApply() {
    if (isApplyBlocked) return;
    onApply?.(values);
  }

  const hasChangedFilters = valueKeys.some(
    (key) => (values[key] ?? '') !== (defaultValues[key] ?? ''),
  );

  const areButtonsAloneInRow = columnCount > 1 && fields.length % columnCount === 0;
  const lastRowStart = areButtonsAloneInRow ? fields.length - columnCount : fields.length;
  const gridFields = fields.slice(0, lastRowStart);
  const lastRowFields = fields.slice(lastRowStart);

  function renderField(field: FilterField) {
    return (
      <FilterFieldItem
        key={field.key}
        field={field}
        values={values}
        onChange={onChange}
        onSubmit={handleApply}
      />
    );
  }

  function renderApplyButton() {
    const applyButton = (
      <Button
        type="button"
        size="sm"
        onClick={handleApply}
        disabled={!hasPendingChanges || isApplyBlocked}
        className={PANEL_BUTTON_CLASS}
      >
        <Check className="size-3.5" aria-hidden />
        Apply
      </Button>
    );
    if (!isApplyBlocked) return applyButton;

    return (
      <DisabledHint hint={toMissingValueHint(missingRequiredFields)} ariaLabel="Apply">
        {applyButton}
      </DisabledHint>
    );
  }

  const panelButtons = (
    <div className="flex shrink-0 items-end gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleReset}
        disabled={!hasChangedFilters}
        className={PANEL_BUTTON_CLASS}
      >
        <RotateCcw className="size-3.5" aria-hidden />
        Reset
      </Button>
      {onApply ? renderApplyButton() : null}
    </div>
  );

  return (
    <div className="rounded-xl bg-card px-4 py-3 ring-1 ring-primary/12 sm:px-5">
      <div ref={gridRef}>
        <FilterFieldsGrid>
          {gridFields.map(renderField)}
          {lastRowFields.length === 0 ? (
            panelButtons
          ) : (
            <div className="col-span-full flex items-end gap-3">
              {lastRowFields.map((field) => (
                <div key={field.key} className="min-w-0 flex-1">
                  {renderField(field)}
                </div>
              ))}
              {panelButtons}
            </div>
          )}
        </FilterFieldsGrid>
      </div>
    </div>
  );
}
